import { useEffect, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { cn } from '../lib/cn';

type ThemeChoice = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'theme';

function readStoredTheme(): ThemeChoice {
  if (typeof window === 'undefined') return 'system';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    // ignore (e.g. localStorage disabled)
  }
  return 'system';
}

function prefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Resolve a ThemeChoice to a concrete light/dark value, then toggle the
 * `dark` class on the document root.
 */
function applyTheme(choice: ThemeChoice): void {
  if (typeof document === 'undefined') return;
  const isDark = choice === 'dark' || (choice === 'system' && prefersDark());
  document.documentElement.classList.toggle('dark', isDark);
}

/**
 * Three-state segmented theme control for the public auth pages.
 *
 * - Persists the user's choice in `localStorage` under key `theme`.
 * - When set to `system`, follows the OS preference live via `matchMedia`.
 * - Applies the theme by toggling the `dark` class on `<html>`.
 */
export function AuthThemeToggle() {
  const [choice, setChoice] = useState<ThemeChoice>(() => readStoredTheme());

  // On mount: apply whatever is stored (covers SSR-rendered pages where the
  // initial className may not match the user's saved preference).
  useEffect(() => {
    applyTheme(choice);
    try {
      window.localStorage.setItem(STORAGE_KEY, choice);
    } catch {
      // ignore
    }
  }, [choice]);

  // Live-follow the OS when set to `system`.
  useEffect(() => {
    if (choice !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('system');
    // Older Safari uses addListener/removeListener.
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [choice]);

  const options: Array<{
    value: ThemeChoice;
    label: string;
    title: string;
    Icon: typeof Sun;
  }> = [
    { value: 'light', label: 'Light', title: 'Light theme', Icon: Sun },
    { value: 'dark', label: 'Dark', title: 'Dark theme', Icon: Moon },
    { value: 'system', label: 'System', title: 'System', Icon: Monitor },
  ];

  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={cn(
        'inline-flex items-center h-8 p-0.5 rounded-full',
        'bg-zinc-100 dark:bg-zinc-800',
        'border border-zinc-200 dark:border-zinc-700',
      )}
    >
      {options.map(({ value, title, Icon }) => {
        const selected = choice === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={title}
            title={title}
            onClick={() => setChoice(value)}
            className={cn(
              'inline-flex items-center justify-center w-7 h-7 rounded-full transition',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand dark:focus-visible:ring-brand-dm',
              selected
                ? 'bg-zinc-900 text-white dark:bg-zinc-50 dark:text-zinc-950 shadow-xs'
                : 'text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100',
            )}
          >
            <Icon className="w-3.5 h-3.5" />
          </button>
        );
      })}
    </div>
  );
}
